//@flow
import React, { lazy } from 'react';
import AttributionText from 'app/common/react/components/DeprecatedPinRep/attribution/AttributionText';
import PinCredits from 'app/common/react/components/DeprecatedPinRep/attribution/PinCredits';
import SafeSuspense from 'app/packages/safe-suspense';
import type { AttributedPin } from 'app/common/react/components/DeprecatedPinRep/attribution/types';
import zIndices from 'app/common/react/components/DeprecatedPinRep/styles/zIndices';
import { Box } from 'gestalt';

const BoardActivityPinRepReactions = lazy(() =>
  import(/* webpackChunkName: "BoardActivityPinRepReactionsCommon" */ 'app/common/react/components/BoardActivities/BoardActivityPinRepReactions'),
);

type Props = {|
  feedbackType: string,
  hideAttribution?: boolean,
  imageOnly?: boolean,
  imageOnlyAttribution?: boolean,
  isHovering: boolean,
  isVideo: boolean,
  onClickThrough: (isOneTap?: boolean) => void,
  onReactionReplyClick: (url: string) => () => void,
  pin: AttributedPin,
  showCredits?: boolean,
  showPinReactionsReply?: boolean,
  sourceUrl: string,
  viewParameter?: $Values<PCONST.thrift.ViewParameterType>,
  viewType?: $Values<PCONST.thrift.ViewType>,
|};

const getActivityIdFromPin = (pin: AttributedPin) =>
  (pin && pin.board_activity && pin.board_activity.id) || pin.id;

function PinAttribution({
  pin,
  feedbackType,
  hideAttribution,
  imageOnlyAttribution,
  isHovering,
  imageOnly: isInImageOnly,
  isVideo,
  onClickThrough,
  onReactionReplyClick,
  showCredits,
  showPinReactionsReply,
  sourceUrl,
  viewParameter,
  viewType,
}: Props) {
  // PinCredits
  const hasPinCredits =
    pin.promoter || (!isInImageOnly && (showCredits || feedbackType === 'followed'));
  // Reactions from Board Activity
  const showReactions = pin.board && pin.board.should_show_board_activity;
  let showPinCredits = hasPinCredits && !hideAttribution;

  // Legal requirement to show "Promoted by" on prommoted pins, overrides the "hideAttribution" prop
  if (feedbackType === 'promoted' || (pin.promoter && !pin.is_downstream_promotion)) {
    showPinCredits = true;
  }

  const activityId = getActivityIdFromPin(pin);

  return (
    <React.Fragment>
      <AttributionText
        pin={pin}
        feedbackType={feedbackType}
        imageOnlyAttribution={imageOnlyAttribution}
        isHovering={isHovering}
        isVideo={isVideo}
        onClickThrough={onClickThrough}
        sourceUrl={sourceUrl}
      />

      {/* user credit */}
      {(hasPinCredits || showReactions) && (
        <Box
          dangerouslySetInlineStyle={{
            __style: {
              zIndex: zIndices.max,
              position: 'relative',
            },
          }}
          paddingY={1}
        >
          {!showReactions &&
            showPinCredits && (
              <PinCredits
                hideTitle={!!showReactions}
                pin={pin}
                viewParameter={viewParameter}
                viewType={viewType}
              />
            )}
          {showReactions && (
            <Box position="relative" data-test-id="board-activity-reactions" minHeight={16}>
              <SafeSuspense>
                <BoardActivityPinRepReactions
                  pinId={pin.id}
                  board={pin.board}
                  activityId={activityId}
                  showReply={showPinReactionsReply}
                />
              </SafeSuspense>
            </Box>
          )}
        </Box>
      )}
    </React.Fragment>
  );
}

export default PinAttribution;
